import React, { useMemo, useState } from 'react';
import { Cake, Check, ChevronDown, ChevronUp, PartyPopper } from 'lucide-react';
import dayjs from 'dayjs';
import { UserAvatar } from '../component/UserAvatar';
import { COLLEAGUES, upcomingOccasions, useWishesSent, whenLabel, type Occasion } from './rightPanelData';

const esc = (t: string) => t.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));

/** Nội dung bài chúc soạn sẵn, có @nhắc tên người được chúc */
export function wishHtml(o: Occasion): string {
  const tag = `<span class="mention" contenteditable="false" data-mention-id="${esc(o.person.id)}" data-user-id="">@${esc(o.person.name)}</span>`;
  return o.kind === 'birthday'
    ? `<p>🎂 Chúc mừng sinh nhật ${tag}! Chúc bạn tuổi mới thật nhiều sức khoẻ, niềm vui và thành công 🎉</p>`
    : `<p>🎉 Chúc mừng ${tag} tròn ${o.years} năm gắn bó cùng công ty! Cảm ơn bạn vì những đóng góp trong suốt thời gian qua 💐</p>`;
}

/** Sinh nhật & kỷ niệm ngày vào làm của đồng nghiệp (khác "Thông báo hệ thống" ở sidebar trái) */
export function BirthdayWidget({ meId, onWish }: { meId: string; onWish: (o: Occasion) => void }) {
  const [wide, setWide] = useState(false);
  const { isSent } = useWishesSent(meId);
  const days = wide ? 30 : 7;
  const list = useMemo(() => upcomingOccasions(COLLEAGUES, dayjs(), days), [days]);
  const todayCount = list.filter(o => o.daysUntil === 0).length;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-4 py-3 flex items-center gap-2 border-b border-gray-100 dark:border-gray-700">
        <Cake className="w-4 h-4 text-pink-500 flex-shrink-0" />
        <span className="font-bold text-pink-600 dark:text-pink-400 text-sm md:text-[15px] flex-1">Sinh nhật & Kỷ niệm</span>
        {todayCount > 0 && <span className="text-[11px] font-bold bg-pink-500 text-white rounded-full px-2 py-0.5">{todayCount} hôm nay</span>}
      </div>
      {list.length === 0 ? (
        <div className="px-4 py-5 text-sm text-gray-500 text-center">Không có sinh nhật hay kỷ niệm nào trong {days} ngày tới.</div>
      ) : (
        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          {list.map(o => {
            const today = o.daysUntil === 0;
            const sent = isSent(o);
            return (
              <div key={`${o.person.id}-${o.kind}`} className={`px-4 py-3 flex items-center gap-3 ${today ? 'bg-pink-50/60 dark:bg-pink-950/20' : ''}`}>
                <div className="relative flex-shrink-0">
                  <UserAvatar name={o.person.name} src={o.person.avatar} size={36} />
                  <span className="absolute -bottom-1 -right-1 text-sm leading-none">{o.kind === 'birthday' ? '🎂' : '🎉'}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate" title={`${o.person.position} · ${o.person.department}`}>{o.person.name}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {o.kind === 'birthday' ? 'Sinh nhật' : `${o.years} năm gắn bó`} · <span className={today ? 'font-bold text-pink-600' : ''}>{whenLabel(o.daysUntil)}</span>
                    {!today && ` (${o.date.format('DD/MM')})`}
                  </div>
                </div>
                {today && (sent
                  ? <span className="flex items-center gap-1 text-xs font-semibold text-green-600 flex-shrink-0"><Check className="w-3.5 h-3.5" /> Đã chúc</span>
                  : (
                    <button type="button" onClick={() => onWish(o)}
                      className="flex items-center gap-1 text-xs font-bold text-white bg-pink-500 hover:bg-pink-600 rounded-full px-2.5 py-1 flex-shrink-0 cursor-pointer">
                      <PartyPopper className="w-3.5 h-3.5" /> Chúc mừng
                    </button>
                  ))}
              </div>
            );
          })}
        </div>
      )}
      <button type="button" onClick={() => setWide(!wide)}
        className="w-full px-4 py-2.5 border-t border-gray-100 dark:border-gray-700 text-xs font-bold text-green-600 hover:bg-gray-50 dark:hover:bg-gray-700/40 flex items-center justify-center gap-1 cursor-pointer">
        {wide ? <>Chỉ xem 7 ngày tới <ChevronUp className="w-3 h-3" /></> : <>Xem cả 30 ngày tới <ChevronDown className="w-3 h-3" /></>}
      </button>
    </div>
  );
}
